import { create } from "zustand";
import type { PlacementMode } from "../domain/placement";

/**
 * Session-scoped undo/redo history for the interior editor (RoomCanvas).
 *
 * A revision is a normalized snapshot of the room's furniture layout — only the
 * fields that affect placement (see FurnitureSnapshotItem). The store is a plain
 * linear stack with a cursor: every editor action pushes a revision, undo/redo
 * move the cursor, and RoomCanvas replays the targeted revision against the
 * backend. History lives only while editing a room and is cleared on exit
 * (matches Figma/Photoshop-style ephemeral undo).
 */

export interface FurnitureSnapshotItem {
  id: number;
  is_placed: boolean;
  x: number | null;
  y: number | null;
  is_mirrored: boolean;
  placement_mode: PlacementMode;
  rug_z_order: number;
  wall_floor_offset: number;
  room_index: number;
}

/** Furniture layout at a point in time, sorted by id for stable comparison. */
export type InteriorSnapshot = FurnitureSnapshotItem[];

/** Cap to keep memory bounded during long editing sessions. */
const MAX_REVISIONS = 100;

const signature = (snap: InteriorSnapshot): string => JSON.stringify(snap);

interface InteriorHistoryState {
  mapObjectId: number | null;
  revisions: InteriorSnapshot[];
  cursor: number;

  /** Seed history for a room. No-op if this room is already initialized. */
  init: (mapObjectId: number, baseline: InteriorSnapshot) => void;
  /** Push a new revision (drops the redo tail). Ignored if equal to current. */
  record: (snapshot: InteriorSnapshot) => void;
  /** Step back one revision; returns the target snapshot or null at the boundary. */
  undo: () => InteriorSnapshot | null;
  /** Step forward one revision; returns the target snapshot or null at the boundary. */
  redo: () => InteriorSnapshot | null;
  /** Clear all history (call on leaving the room). */
  reset: () => void;
}

const useInteriorHistoryStore = create<InteriorHistoryState>((set, get) => ({
  mapObjectId: null,
  revisions: [],
  cursor: -1,

  init: (mapObjectId, baseline) =>
    set((state) => {
      if (state.mapObjectId === mapObjectId && state.revisions.length > 0) {
        return state; // already initialized for this room
      }
      return { mapObjectId, revisions: [baseline], cursor: 0 };
    }),

  record: (snapshot) =>
    set((state) => {
      if (state.cursor < 0 || state.revisions.length === 0) {
        // Not initialized — treat first record as the baseline.
        return { revisions: [snapshot], cursor: 0 };
      }
      if (signature(state.revisions[state.cursor]) === signature(snapshot)) {
        return state; // no meaningful change
      }
      // Drop any redo tail, then append.
      let revisions = state.revisions.slice(0, state.cursor + 1);
      revisions.push(snapshot);
      // Enforce cap by dropping the oldest entries.
      if (revisions.length > MAX_REVISIONS) {
        revisions = revisions.slice(revisions.length - MAX_REVISIONS);
      }
      return { revisions, cursor: revisions.length - 1 };
    }),

  undo: () => {
    const { cursor, revisions } = get();
    if (cursor <= 0) return null;
    const next = cursor - 1;
    set({ cursor: next });
    return revisions[next];
  },

  redo: () => {
    const { cursor, revisions } = get();
    if (cursor >= revisions.length - 1) return null;
    const next = cursor + 1;
    set({ cursor: next });
    return revisions[next];
  },

  reset: () => set({ mapObjectId: null, revisions: [], cursor: -1 }),
}));

export default useInteriorHistoryStore;
